/* eslint-disable max-lines */
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import { checkNdcProductLinkApi, createInventoryApi, listProductNdcsApi } from 'api/inventory';
import { searchActiveCatalogProductsApi } from 'api/drug';
import { brandNameAllowedCharRegex, brandNameAllowedValueRegex } from 'common/constants/regex';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';
import { formatForm, formatPackSize } from 'modules/CatalogManagement/utils/format';
import {
  LINK_CHECK_STATUS,
  countLabel,
  describeExistingItem,
  isLinkCheckRefusal,
  listStrengthFormPack,
  productNameOf,
} from '../utils/ndcProductLink';
import CatalogProductCard from './CatalogProductCard';
import ConfirmNdcProductLinkStep from './ConfirmNdcProductLinkStep';
import ManufacturerSelect from './ManufacturerSelect';
import SetDefaultNdcToggle from './SetDefaultNdcToggle';

const REORDER_MODE = {
  DEFAULT: 'default',
  CUSTOM: 'custom',
};

// POM-6600 — the NDC is linked only after the user reviews the product-to-NDC association.
const STEP = {
  DETAILS: 'details',
  REVIEW: 'review',
};

const STEP_CAPTIONS = {
  [STEP.DETAILS]: 'Step 1 of 2 · Details',
  [STEP.REVIEW]: 'Step 2 of 2 · Review',
};

const listSearchOptionFacts = (option) => [
  { label: 'Product ID', value: option.productId ?? '—' },
  { label: 'Strength', value: option.strength || '—' },
  { label: 'Form', value: formatForm(option.formAlias, option.dosageForm) },
  { label: 'Pack', value: formatPackSize(option.packSizeValue, option.packSizeUnit) },
];

const describeLinkedNdcCount = (linkedNdcs, lookupDone) => {
  if (!lookupDone) return 'Checking…';
  if (!linkedNdcs) return '—';
  return linkedNdcs.length ? countLabel(linkedNdcs.length, 'NDC', 'NDCs') : 'None yet';
};

// Mirrors AddStockDialog.js's NDC_REGEX/isValidNdc - same accepted formats
// (10-11 digit, or hyphenated 4-5/3-4/1-2) across every NDC input in this module.
const NDC_REGEX = /^\d{10,11}$|^(\d{4,5}-\d{3,4}-\d{1,2})$/;
const isValidNdc = (value) => NDC_REGEX.test(String(value).trim());

// Non-empty but malformed: show live, since Review association is disabled while ndcValid
// is false and would otherwise never be clicked to set `submitted`.
const getNdcHelperText = (ndc, ndcValid, submitted) => {
  if (ndc.trim().length > 0 && !ndcValid) return 'Invalid NDC format';
  if (submitted && ndc.trim().length === 0) return 'Required';
  return '';
};

const AddInventoryItemDialog = ({ open, onClose, onCreated }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [catalogOptions, setCatalogOptions] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const catalogTimerRef = useRef(null);

  const [drugName, setDrugName] = useState('');
  const [strength, setStrength] = useState('');
  const [ndc, setNdc] = useState('');

  // POM-5910 — manufacturer is a pick-list rather than free text; the options
  // and their loading state live in ManufacturerSelect. `null` (not '') is the
  // empty value so the MUI Autocomplete stays controlled.
  const [manufacturer, setManufacturer] = useState(null);
  const [reorderMode, setReorderMode] = useState(REORDER_MODE.DEFAULT);
  const [lowStock, setLowStock] = useState('');
  const [criticalStock, setCriticalStock] = useState('');
  const [leadTime, setLeadTime] = useState('');

  // POM-5707 — a brand new NDC has no facility stock by definition, so it cannot
  // be promoted from the inventory grid's Edit Default NDC dialog until someone
  // receives stock against it. Offering the choice at creation time closes that
  // gap; listProductNdcs tells us which NDC (if any) currently holds the flag.
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [linkedNdcs, setLinkedNdcs] = useState(null);
  const [defaultLookupDone, setDefaultLookupDone] = useState(false);
  const currentDefault = linkedNdcs?.find((linkedNdc) => linkedNdc.isDefault) ?? null;

  const [step, setStep] = useState(STEP.DETAILS);
  const [linkCheck, setLinkCheck] = useState(null);
  const [linkCheckStatus, setLinkCheckStatus] = useState(null);
  const [existingItem, setExistingItem] = useState(null);
  // Bumped on every check, on Back to edit and on reopen, so a late answer from an earlier check
  // can never move the dialog.
  const linkCheckRequestRef = useRef(0);

  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (open) {
      setCatalogOptions([]);
      setSelectedCatalog(null);
      setDrugName('');
      setStrength('');
      setManufacturer(null);
      setNdc('');
      setReorderMode(REORDER_MODE.DEFAULT);
      setLowStock('');
      setCriticalStock('');
      setLeadTime('');
      setSetAsDefault(false);
      setLinkedNdcs(null);
      setDefaultLookupDone(false);
      setStep(STEP.DETAILS);
      setLinkCheck(null);
      setLinkCheckStatus(null);
      setExistingItem(null);
      linkCheckRequestRef.current += 1;
      setSubmitted(false);
      setSubmitError('');
    }
  }, [open]);

  const catalogProductId = selectedCatalog?.productId ?? null;

  useEffect(() => {
    if (!open || !catalogProductId) return undefined;

    let cancelled = false;
    setDefaultLookupDone(false);

    listProductNdcsApi(catalogProductId)
      .then((options) => {
        if (cancelled) return;
        setLinkedNdcs(options);
      })
      .catch(() => {
        // A failed lookup only costs the "replaces X" hint and the linked-NDC count
        // — the checkbox still works, and the backend clears any existing default
        // transactionally.
        if (!cancelled) setLinkedNdcs(null);
      })
      .finally(() => {
        if (!cancelled) setDefaultLookupDone(true);
      });

    return () => {
      cancelled = true;
    };
  }, [open, catalogProductId]);

  const handleCatalogInputChange = (_, inputVal, reason) => {
    if (reason !== 'input') return;
    clearTimeout(catalogTimerRef.current);
    if (inputVal.trim().length < 3) {
      setCatalogLoading(false);
      setCatalogOptions([]);
      return;
    }
    setCatalogLoading(true);
    catalogTimerRef.current = setTimeout(() => {
      searchActiveCatalogProductsApi(inputVal.trim())
        .then(setCatalogOptions)
        .catch(() => {
          // A failed search reads as "no matches" rather than an unhandled rejection; the user
          // can retype to retry, and Add Catalog Item is still available.
          setCatalogOptions([]);
        })
        .finally(() => setCatalogLoading(false));
    }, 300);
  };

  const handleSelectCatalog = (option) => {
    setSelectedCatalog(option);
    const packSize =
      option?.packSizeValue && option?.packSizeUnit
        ? `${option.packSizeValue}${option.packSizeUnit}`
        : '';
    setStrength([option?.catalogStrength, packSize].filter(Boolean).join(' '));
    setCatalogOptions([]);
    // The default flag is per product — switching products invalidates both the
    // choice and the "replaces X" hint.
    setSetAsDefault(false);
    setLinkedNdcs(null);
  };

  const lowNum = parseFloat(lowStock);
  const criticalNum = parseFloat(criticalStock);
  const leadNum = parseFloat(leadTime);

  const lowValid = lowStock !== '' && !Number.isNaN(lowNum) && lowNum > 0 && lowNum <= 100;
  const leadValid = leadTime !== '' && !Number.isNaN(leadNum) && leadNum >= 0;
  const criticalProvided = criticalStock !== '';
  const criticalValid =
    !criticalProvided ||
    (!Number.isNaN(criticalNum) && criticalNum >= 0 && (!lowValid || criticalNum <= lowNum));

  const reorderValid =
    reorderMode === REORDER_MODE.DEFAULT || (lowValid && leadValid && criticalValid);

  const ndcValid = isValidNdc(ndc);
  const drugNameValid = brandNameAllowedValueRegex.test(drugName);

  const canSave =
    Boolean(selectedCatalog) &&
    drugName.trim().length > 0 &&
    drugNameValid &&
    strength.trim().length > 0 &&
    Boolean(manufacturer) &&
    ndcValid &&
    reorderValid;

  const handleClose = () => {
    setSubmitted(false);
    onClose();
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!canSave) return;
    setSaving(true);
    setSubmitError('');

    const input = {
      catalogId: selectedCatalog.catalogId,
      drugName: drugName.trim(),
      strength: strength.trim(),
      // Sent as a plain String exactly as before — the dropdown constrains the
      // input, it does not change how the value is stored.
      manufacturer,
      ndc: ndc.trim(),
      ...(setAsDefault ? { setAsDefault: true } : {}),
      ...(reorderMode === REORDER_MODE.CUSTOM
        ? {
            reorderConfig: {
              lowStockPercent: lowNum,
              leadTime: leadNum,
              ...(criticalProvided ? { criticalStockPercent: criticalNum } : {}),
            },
          }
        : {}),
    };

    createInventoryApi(input)
      .then((createdItem) => {
        setSaving(false);
        enqueueSnackbar(
          setAsDefault
            ? `Inventory item "${drugName}" created and set as the default NDC`
            : `Inventory item "${drugName}" created`,
          { variant: 'success' }
        );
        onCreated(createdItem);
        handleClose();
      })
      .catch((error) => {
        setSaving(false);
        // Surface the backend validation error inline; keep the form open and
        // editable so the user can correct the input and resubmit (POM-5739).
        setSubmitError(getGraphQLErrorMessage(error));
      });
  };

  const isCheckingLink = linkCheckStatus === LINK_CHECK_STATUS.LOADING;

  // POM-6600 — the read-only check behind the review step. An NDC already in inventory keeps the
  // user on the form with the error under the NDC field. A failed check still opens the review
  // step, which offers Retry and still allows Confirm, so stock intake is never blocked.
  const runLinkCheck = () => {
    linkCheckRequestRef.current += 1;
    const requestId = linkCheckRequestRef.current;
    const isLatestRequest = () => requestId === linkCheckRequestRef.current;
    setLinkCheckStatus(LINK_CHECK_STATUS.LOADING);

    checkNdcProductLinkApi({
      catalogId: selectedCatalog.catalogId,
      ndc: ndc.trim(),
      drugName: drugName.trim(),
      manufacturer,
    })
      .then((result) => {
        if (!isLatestRequest()) return;
        setLinkCheck(result);
        setLinkCheckStatus(LINK_CHECK_STATUS.LOADED);
        if (result.existingItem) {
          setExistingItem(result.existingItem);
          setStep(STEP.DETAILS);
          return;
        }
        setStep(STEP.REVIEW);
      })
      .catch((error) => {
        if (!isLatestRequest()) return;
        setLinkCheck(null);
        if (isLinkCheckRefusal(error)) {
          setLinkCheckStatus(null);
          setSubmitError(getGraphQLErrorMessage(error));
          setStep(STEP.DETAILS);
          return;
        }
        setLinkCheckStatus(LINK_CHECK_STATUS.FAILED);
        setStep(STEP.REVIEW);
      });
  };

  const handleReviewLink = () => {
    setSubmitted(true);
    if (!canSave) return;
    setSubmitError('');
    runLinkCheck();
  };

  const handleBackToEdit = () => {
    linkCheckRequestRef.current += 1;
    setLinkCheckStatus(null);
    setSubmitError('');
    setStep(STEP.DETAILS);
  };

  // Block backdrop-click dismissal, and prevent any close (incl. Escape) while a
  // create is in flight — the only way out is the Cancel button (POM-5739).
  const handleDialogClose = (_, reason) => {
    if (saving || reason === 'backdropClick') return;
    handleClose();
  };

  const isReviewStep = step === STEP.REVIEW;

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth={isReviewStep ? 'md' : 'sm'} fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        {isReviewStep ? (
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              Confirm product-to-NDC association
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {[
                `You are linking NDC ${ndc.trim()} to ${productNameOf(selectedCatalog)}`,
                `(${selectedCatalog.productId}). Check the drug, strength, form, package size,`,
                'and manufacturer before confirming. This association cannot be undone from',
                'this screen.',
              ].join(' ')}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              Add Inventory Item
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Create a new inventory item and link it to a catalog drug identity.
            </Typography>
          </Box>
        )}
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ fontWeight: 600, whiteSpace: 'nowrap', pt: 0.5 }}
        >
          {STEP_CAPTIONS[step]}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {submitError && (
          <Alert severity='error' onClose={() => setSubmitError('')} sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {isReviewStep && (
          <ConfirmNdcProductLinkStep
            selectedCatalog={selectedCatalog}
            enteredNdc={ndc.trim()}
            enteredDrugName={drugName.trim()}
            enteredStrength={strength.trim()}
            enteredManufacturer={manufacturer}
            isSetAsDefault={setAsDefault}
            currentDefaultNdc={currentDefault?.ndc}
            linkCheck={linkCheck}
            linkCheckStatus={linkCheckStatus}
            onRetry={runLinkCheck}
          />
        )}

        {/* Step 1 stays mounted behind the review step, so Back to edit returns to every
            value exactly as left and the manufacturer list is not fetched again. */}
        <Box
          sx={{
            display: isReviewStep ? 'none' : 'block',
            p: 1.5,
            mb: 3,
            bgcolor: 'action.hover',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant='caption'
            sx={{ fontWeight: 600, display: 'block', mb: 1.5, color: 'text.secondary' }}
          >
            CATALOG ASSOCIATION
          </Typography>

          <Autocomplete
            size='small'
            fullWidth
            loading={catalogLoading}
            options={catalogOptions}
            filterOptions={(x) => x}
            value={selectedCatalog}
            isOptionEqualToValue={(opt, val) => opt.catalogId === val?.catalogId}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : (opt.displayName ?? ''))}
            onInputChange={handleCatalogInputChange}
            onChange={(_, option) => handleSelectCatalog(option)}
            renderOption={(props, opt) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <li {...props} key={opt.catalogId}>
                <Box>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {opt.displayName}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    component='div'
                    sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5 }}
                  >
                    {listSearchOptionFacts(opt).map(({ label, value }) => (
                      <span key={label}>
                        {label}{' '}
                        <Box component='strong' sx={{ color: 'text.primary' }}>
                          {value}
                        </Box>
                      </span>
                    ))}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                label='Search product by drug name *'
                placeholder='e.g. Paracetamol, Amoxicillin...'
                error={submitted && !selectedCatalog}
                helperText={submitted && !selectedCatalog ? 'Select a product to continue' : ''}
                sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {catalogLoading && <CircularProgress color='inherit' size={14} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* POM-5910 / POM-6600 — the search dropdown closes on selection, so the
              picked product's details are echoed back for the user to check which
              catalog identity the new NDC is being bound to. */}
          {selectedCatalog && (
            <Box sx={{ mt: 1.5 }}>
              <CatalogProductCard
                label='SELECTED PRODUCT'
                hasCheckIcon
                testId='selected-catalog-product'
                productName={productNameOf(selectedCatalog)}
                productId={selectedCatalog.productId}
                columnCount={3}
                facts={[
                  ...listStrengthFormPack(selectedCatalog),
                  { label: 'Drug name', value: selectedCatalog.drugName || '—' },
                  {
                    label: 'NDCs already linked',
                    value: describeLinkedNdcCount(linkedNdcs, defaultLookupDone),
                  },
                ]}
              />
            </Box>
          )}
        </Box>

        {selectedCatalog && (
          <Box sx={{ display: isReviewStep ? 'none' : 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              size='small'
              fullWidth
              label='Drug Name (brand) *'
              placeholder='e.g. Dolo'
              value={drugName}
              onChange={(e) => setDrugName(e.target.value)}
              inputProps={{
                onKeyDown: (e) => {
                  if (e.key.length === 1 && !brandNameAllowedCharRegex.test(e.key)) {
                    e.preventDefault();
                  }
                },
              }}
              error={(submitted && drugName.trim().length === 0) || (submitted && !drugNameValid)}
              helperText={
                submitted && !drugNameValid
                  ? 'Must be alphanumeric / - / _ / . / () / \\ / / / +'
                  : 'Brand name for this stocked item. Different brands can share the same product identity.'
              }
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                size='small'
                fullWidth
                label='Strength *'
                placeholder='e.g. 250 mg'
                value={strength}
                onChange={(e) => setStrength(e.target.value)}
                error={submitted && strength.trim().length === 0}
              />
              <ManufacturerSelect
                value={manufacturer}
                onChange={setManufacturer}
                showRequiredError={submitted && !manufacturer}
              />
            </Box>

            <TextField
              size='small'
              fullWidth
              label='NDC *'
              value={ndc}
              onChange={(e) => {
                setNdc(e.target.value);
                setExistingItem(null);
              }}
              error={Boolean(existingItem) || (ndc.trim().length > 0 ? !ndcValid : submitted)}
              helperText={
                existingItem
                  ? `An inventory item already exists for NDC ${existingItem.ndc}`
                  : getNdcHelperText(ndc, ndcValid, submitted)
              }
            />

            {catalogProductId && (
              <SetDefaultNdcToggle
                productId={catalogProductId}
                currentDefault={currentDefault}
                lookupDone={defaultLookupDone}
                checked={setAsDefault}
                onChange={setSetAsDefault}
              />
            )}

            <Box>
              <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                Reorder Config (optional)
              </Typography>
              <RadioGroup
                row
                value={reorderMode}
                onChange={(e) => setReorderMode(e.target.value)}
                sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap' }}
              >
                {[
                  { value: REORDER_MODE.DEFAULT, label: 'Use system default' },
                  { value: REORDER_MODE.CUSTOM, label: 'Custom threshold' },
                ].map((option) => (
                  <Box
                    key={option.value}
                    sx={{
                      flex: 1,
                      border: '1px solid',
                      borderColor: reorderMode === option.value ? 'primary.main' : 'divider',
                      bgcolor: reorderMode === option.value ? 'action.selected' : 'transparent',
                      borderRadius: 1,
                      px: 1,
                    }}
                  >
                    <FormControlLabel
                      value={option.value}
                      control={<Radio size='small' />}
                      label={
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                          {option.label}
                        </Typography>
                      }
                      sx={{ m: 0, width: '100%', py: 0.5 }}
                    />
                  </Box>
                ))}
              </RadioGroup>
            </Box>

            {reorderMode === REORDER_MODE.CUSTOM && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  type='number'
                  size='small'
                  fullWidth
                  label='Low Stock % *'
                  placeholder='e.g. 50'
                  value={lowStock}
                  onChange={(e) => setLowStock(e.target.value)}
                  error={submitted && !lowValid}
                />
                <TextField
                  type='number'
                  size='small'
                  fullWidth
                  label='Critical Stock %'
                  placeholder='e.g. 10'
                  value={criticalStock}
                  onChange={(e) => setCriticalStock(e.target.value)}
                  error={criticalProvided && !criticalValid}
                />
                <TextField
                  type='number'
                  size='small'
                  fullWidth
                  label='Lead Time (days) *'
                  placeholder='e.g. 14'
                  value={leadTime}
                  onChange={(e) => setLeadTime(e.target.value)}
                  error={submitted && !leadValid}
                />
              </Box>
            )}

            {/* Repeats the NDC field's error next to the button that was pressed, and names the
                product that already holds the NDC. An error, not a warning: the user cannot go
                on until the NDC changes. */}
            {existingItem && (
              <Alert severity='error' data-testid='existing-ndc-banner'>
                <AlertTitle sx={{ fontWeight: 700 }}>NDC already in inventory</AlertTitle>
                {describeExistingItem({ existingItem, pickedProductId: catalogProductId })}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        {isReviewStep && (
          <Typography variant='body2' color='text.secondary' sx={{ mr: 'auto' }}>
            Not the right product? Use Back to edit and pick another one on the form.
          </Typography>
        )}
        <Button onClick={handleClose} color='inherit' disabled={saving}>
          Cancel
        </Button>
        {isReviewStep ? (
          <>
            <Button variant='outlined' onClick={handleBackToEdit} disabled={saving}>
              Back to edit
            </Button>
            <Button variant='contained' onClick={handleSave} disabled={saving}>
              {saving ? 'Creating…' : 'Confirm association'}
            </Button>
          </>
        ) : (
          <Button
            variant='contained'
            onClick={handleReviewLink}
            disabled={!canSave || isCheckingLink || Boolean(existingItem)}
          >
            {isCheckingLink ? 'Checking…' : 'Review association'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

AddInventoryItemDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};
AddInventoryItemDialog.defaultProps = {
  onCreated: () => {},
};

export default AddInventoryItemDialog;
